<template>
  <div class="template-management-container">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <span>{{ $t("message.template.title") }}</span>
          <el-button v-if="canAddDeviceTemplate" type="primary" @click="handleAddTemplate">
            {{ $t("message.template.add") }}
          </el-button>
        </div>
        <!-- Search Form -->
        <el-form :inline="true" :model="queryParams" class="search-form">
          <el-form-item :label="$t('message.template.company')">
            <el-tree-select
              v-model="queryParams.companyId"
              :data="companyTreeData"
              :props="treeProps"
              :placeholder="$t('message.template.selectCompany')"
              check-strictly
              clearable
              filterable
              style="width: 200px"
              @change="handleSearch"
            />
          </el-form-item>

          <el-form-item :label="$t('message.template.name')">
            <el-input
              v-model="queryParams.name"
              :placeholder="$t('message.template.inputName')"
              clearable
              @change="handleSearch"
              @keyup.enter="handleSearch"
            />
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="handleSearch">
              {{ $t("message.common.search") }}
            </el-button>
            <el-button @click="resetSearch">
              {{ $t("message.template.reset") }}
            </el-button>
          </el-form-item>
        </el-form>
      </template>

      <el-table :data="templateList" border style="width: 100%">
        <el-table-column
          v-if="isColumnVisible"
          prop="id"
          :label="$t('message.template.id')"
          width="80"
          sortable
        />
        <el-table-column
          prop="name"
          :label="$t('message.template.name')"
          width="150"
          sortable
        />
        <el-table-column
          prop="company_name"
          :label="$t('message.template.company')"
          width="180"
          sortable
        />
        <el-table-column
          prop="get_type_name"
          :label="$t('message.template.getType')"
          width="180"
          v-if="isColumnVisible"
          sortable
        />
        <el-table-column
          prop="var_total"
          :label="$t('message.template.varTotal')"
          width="120"
          sortable
        />
        <el-table-column
          prop="device_total"
          :label="$t('message.template.deviceTotal')"
          width="120"
          sortable
        />
        <el-table-column
          prop="created_at"
          :label="$t('message.template.createTime')"
          width="180"
          sortable
        >
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>

        <el-table-column :label="$t('message.template.actions')" width="180">
          <template #default="scope">
            <el-button v-if="canEditDeviceTemplate" size="small" @click="handleEditTemplate(scope.row)">
              {{ $t("message.template.edit") }}
            </el-button>
            <el-button
             v-if="canDeleteDeviceTemplate"
              size="small"
              type="danger"
              @click="handleDeleteTemplate(scope.row)"
            >
              {{ $t("message.template.delete") }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.currentPage"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[5, 10, 15, 20, 30]"
          layout="total, sizes, prev, pager, next, jumper"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <!-- Template Edit Dialog          -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="70%"
      height="100%"
      :close-on-click-modal="false"
      @opened="handleDialogOpened"
    >
      <template-form
        v-if="dialogVisible"
        ref="templateFormRef"
        :template-data="currentTemplate"
        :slave-tabs-data="initialSlaveTabs"
        @submit="handleSubmitTemplate"
      />

      <template #footer>
        <el-button @click="dialogVisible = false">
          {{ $t("message.template.cancel") }}
        </el-button>
        <el-button type="primary" @click="submitTemplateForm">
          {{ $t("message.template.save") }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { useI18n } from "vue-i18n";
import TemplateForm from "./TemplateForm.vue";
import {
  getTemplates,
  deleteTemplate,
  getSlavesByTemplate,
  getVariablesByTemplate,
} from "@/api/TemplateManagement";
import { getCompanyTree } from "@/api/CompanyManager";
import { usePermission } from '@/composables/usePermission'

const {  canAddDeviceTemplate,canEditDeviceTemplate,canDeleteDeviceTemplate} = usePermission()
   

const { t } = useI18n();

// 响应式数据
const templateList = ref([]);
const currentTemplate = ref({});
const dialogVisible = ref(false);
const dialogTitle = ref("");
const templateFormRef = ref();
const companyTreeData = ref([]);
const isColumnVisible = ref(false);
/* const defaultVariable = ref({
  id: 0,
  name: '',
  register_type: 3,
  address_one: 0,
  address_two: 0,
  address_three: 0,
  address_four: 0,
  address_five: 0,
  address_six: 0,
  address_decimal: 0,
  register_num: 1,
  data_type_id: 1,
  rw_type_id: 1,
  timed_storage_frequency: 0,
  change_storage_val: 0,
  storage_type_id: 1,
  var_type_id: 1,
  interval: 0,
  var_flag: '',
  unit: '',
  slave_id: 0,
  template_id: 0,
  company_id: 0  
}) */

const defaultSlave = ref({
  id: 0,
  name: t("message.slave.tabName"),
  label: t("message.slave.tabName") + " 1",
  protocol: 1,
  variables: [],
  selectedVariables: [],
});

const initialSlaveTabs = ref([defaultSlave.value]);

// 查询参数
const queryParams = reactive({
  companyId: null,
  name: null,
  pageNum: 1,
  pageSize: 5,
});

const deleteParams = reactive({
  company_id: 0,
  id: 0,
});

const queryCompanyTreeParams = reactive({
  companyId: null,
});

const treeProps = {
  value: "id",
  label: "name",
  children: "children",
};

// 分页
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0,
});

const queryVarParams = reactive({
  template_id: null,
  slave_id: null,
  name: "",
  pageNum: 1,
  pageSize: 5,
});

const collectiontypeOptions = ref([]);

const loadOptions = () => {
  collectiontypeOptions.value = [
    { value: 1, label: t("message.template.getTypeA") },
    { value: 2, label: t("message.template.getTypeB") },
  ];
};

/* const findOptionName = (options, id) => {
  try {
    // 处理响应式对象
    const optionList = options?.value || options
    
    // 验证输入
    if (!Array.isArray(optionList)) {
      console.error('Invalid options:', optionList)
      return ''
    }
    
    // 查找匹配项
    const found = optionList.find(opt => {
      // 兼容 value 可能是数字或字符串的情况
      const optValue = opt?.value ?? opt?.id
      return String(optValue) === String(id)
    })

    // 返回结果或空字符串
    return found?.label || ''
  } catch (error) {
    console.error('Error in findOptionName:', error)
    return ''
  }
}
 */

// 日期格式化
const formatDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(/\//g, "-") // 将斜杠替换为横杠
    .replace(/(\d+):(\d+):(\d+)/, "$1:$2:$3"); // 保持时间格式
};

const getDefaultVariables = () => {
  return []; //[JSON.parse(JSON.stringify(defaultVariable.value))]
};
const getDefaultSlave = () => {
  return [JSON.parse(JSON.stringify(defaultSlave.value))];
};

// 方法定义
const loadCompanyTree = async () => {
  try {
    const res = await getCompanyTree(queryCompanyTreeParams);
    companyTreeData.value = res.data.list;

    if (companyTreeData.value.length > 0 && !queryParams.companyId) {
      queryParams.companyId = companyTreeData.value[0].id;
    }
  } catch (error) {
    ElMessage.error(t("message.gateway.loadCompanyFailed"));
  }
};

const converTemplateData = (apiData) => {
  return apiData.map((item) => ({
    ...item,
  }));
};

/* 
const converTemplateData = (apiData) => {
  return apiData.map(item => ({
    ...item, // 保留原始字段
    get_type_name: findOptionName(collectiontypeOptions.value, item.get_type)
  }));
};
 */
const processVariableData = (apiData) => {
  return apiData.map((item) => ({
    id: item.id,
    name: item.name,
    register:
      item.register_type.toString() +
      item.address_one.toString().padStart(4, "0"),
    register_type: item.register_type,
    address_one: item.address_one || 0,
    address_two: item.address_two || 0,
    address_three: item.address_three || 0,
    address_four: item.address_four || 0,
    address_five: item.address_five || 0,
    address_six: item.address_six || 0,
    address_decimal: item.address_decimal || 0,
    register_num: item.register_num || 1,
    data_type_id: item.data_type_id,
    data_type_name: item.data_type_name,
    rw_type_id: item.rw_type_id,
    rw_type_name: item.rw_type_name,
    storage_type_id: item.storage_type_id,
    storage_type_name: item.storage_type_name,
    var_type_id: item.var_type_id,
    var_type_name: item.var_type_name,
    slave_id: item.slave_id,
    template_id: item.template_id,
    company_id: item.company_id,
  }));
};

const loadVariables = async (template, slave_id) => {
  try {
    queryVarParams.template_id = template.id;
    queryVarParams.slave_id = slave_id;

    const response = await getVariablesByTemplate(queryVarParams);

    if (response.success && response.data?.list.length > 0) {
      return processVariableData(response.data.list);
    }
    return getDefaultVariables();
  } catch (error) {
    ElMessage.error(error.message || t("message.slave.variableLoadFailed"));
    return getDefaultVariables();
  }
};

const loadSlaves = async (template) => {
  try {
    const slaveParm = {
      templateId: template.id,
      companyId: queryParams.companyId,
    };

    const response = await getSlavesByTemplate(slaveParm);
        
    if (!response.success || !response.data?.list?.length) {
      return getDefaultSlave();
    }

    if (response.success && response.data?.list) {
      return await Promise.all(
        response.data.list.map(async (slave) => {
          let variables = [];
          try {
            variables = await loadVariables(template, slave.id);
            // 如果加载的变量为空，使用默认值
            if (!variables?.length) {
              variables = getDefaultVariables();
            }
          } catch (error) {
            variables = getDefaultVariables();
          }
          return {
            id: slave.id,
            name: slave.name,
            label: slave.name,
            protocol: slave.protocol_id,
            variables: variables,
            selectedVariables: [],
          };
        })
      );
    }
    return getDefaultSlave();
  } catch (error) {
    ElMessage.error(error.message || t("message.slave.slaveLoadFailed"));
    return getDefaultSlave();
  }
};

const fetchTemplates = async () => {
  try {
    // 验证 companyId
    if (!queryParams.companyId || queryParams.companyId <= 0) {
      ElMessage.warning(t("message.template.selectCompanyFirst"));
      return;
    }

    const params = {
      companyId: queryParams.companyId,
      name: queryParams.name,
      page: queryParams.pageNum,
      size: queryParams.pageSize,
    };

    const res = await getTemplates(params);

    // 添加防御性检查
    if (!res?.success) {
      ElMessage.Error(res?.msg || t("message.template.noDataReturned"));
      return;
    }

    templateList.value = converTemplateData(res.data.list || []);
    pagination.total = res.data.totalCount || 0;
  } catch (error) {
    ElMessage.error(error.message || t("message.template.fetchError"));
    templateList.value = [];
    pagination.total = 0;
  }
};

const handleSearch = () => {
  queryParams.pageNum = 1;
  fetchTemplates();
};

const resetSearch = () => {
  queryParams.name = null;
  queryParams.pageNum = 1;
  handleSearch();
};

const handleDialogOpened = () => {
  // 对话框完全打开后的回调
  if (
    dialogTitle.value == t("message.template.addTitle") &&
    templateFormRef.value?.cleanup
  ) {
    templateFormRef.value.cleanup();
  }
};

const handleAddTemplate = () => {
  currentTemplate.value = {
    company_id: queryParams.companyId,
    name: "",
    id: 0,
  };
  initialSlaveTabs.value = getDefaultSlave();
  dialogTitle.value = t("message.template.addTitle");
  dialogVisible.value = true;
  // 不再直接调用 cleanup，由 opened 事件处理
};

const handleEditTemplate = async (template) => {
  try {
    currentTemplate.value = { ...template };
    dialogTitle.value = t("message.template.editTitle");
    const slaves = await loadSlaves(template);
    /*ElMessage.error(`${slaves?.length} 个从机加载11`);
    if(slaves?.length>0)
    {
      ElMessage.error(`${slaves[0].variables?.length} 个变量加载11`);
    }*/

    initialSlaveTabs.value = JSON.parse(JSON.stringify(slaves));

    dialogVisible.value = true;
  } catch (error) {
    ElMessage.error(error.message || t("message.template.loadFailed"));
  }
};

const handleDeleteTemplate = (template) => {
  ElMessageBox.confirm(
    t("message.template.deleteConfirm", { name: template.name }),
    t("message.common.tip"),
    {
      confirmButtonText: t("message.common.confirm"),
      cancelButtonText: t("message.common.cancel"),
      type: "warning",
    }
  )
    .then(async () => {
      try {
        deleteParams.company_id = template.company_id;
        deleteParams.id = template.id;
        const retData = await deleteTemplate(deleteParams);
        if (retData.success) {
          ElMessage.success(t("message.template.deleteSuccess"));
        } else {          
          //ElMessage.error(`${template.id} 个变量加载11`);
          
          ElMessage.error(retData?.msg || t("message.template.deleteFailed"));
        }
        fetchTemplates();
      } catch (error) {
        ElMessage.error(error.message || t("message.template.deleteFailed"));
      }
    })
    .catch(() => {
      ElMessage.info(t("message.common.deleteCancelled"));
    });
};

const submitTemplateForm = async () => {
  const ret = await templateFormRef.value.submitForm();
  if (ret.success) {
    dialogVisible.value = false;
  }
  fetchTemplates();
};

const handleSubmitTemplate = async (formData) => {
  try {
    if (formData.id) {
      // await updateTemplate(formData)
      ElMessage.success(t("message.template.updateSuccess"));
    } else {
      // await createTemplate(formData)
      ElMessage.success(t("message.template.addSuccess"));
    }
    dialogVisible.value = false;
    fetchTemplates();
  } catch (error) {
    ElMessage.error(error.message || t("message.template.submitError"));
  }
};

const handlePageChange = (val) => {
  pagination.currentPage = val;
  fetchTemplates();
};

const handleSizeChange = (val) => {
  pagination.pageSize = val;
  fetchTemplates();
};

// 初始化
onMounted(async () => {
  await loadCompanyTree();
  await loadOptions();
  await handleSearch();
  // fetchTemplates()
});
</script>

<style scoped>
/* 保持原有的style部分不变 */
.template-management-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.search-form {
  margin-top: 15px;

  :deep(.el-form-item) {
    margin-right: 15px;
    margin-bottom: 0;
  }
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .search-form {
    :deep(.el-form-item) {
      margin-right: 0;
      margin-bottom: 10px;
      width: 100%;

      .el-input,
      .el-tree-select {
        width: 100%;
      }
    }
  }
}
.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>
